import React from "react";
import { notification } from "antd";
import { addTestCaseToTestSuite, getTestSuite } from "../action";
import {
  notifyError,
  notifyInProgress,
  notifySuccess,
  notifyWarning,
} from "../../../utils/notification";
import TestSuiteScriptSelectorModal, {
  OnSubmitParams,
} from "./TestSuiteScriptSelectorModal";

interface AddScriptToSuiteModalProps {
  open: boolean;
  close: (open: boolean) => void;
  handleRefresh: () => Promise<any>;
  testSuiteId: string;
}

const AddScriptToSuiteModal = ({
  open,
  close,
  handleRefresh,
  testSuiteId,
}: AddScriptToSuiteModalProps) => {
  const handleAddScript = async ({
    accountId,
    testcaseIds,
    closeModal,
    refresh,
  }: OnSubmitParams) => {
    if (!testSuiteId) {
      notifyError("Test suite not found");
      return;
    }

    const selectedUniqueIds = Array.from(new Set(testcaseIds));
    const testSuite: any = await getTestSuite(accountId, testSuiteId);
    const existingTestcaseIds = new Set(
      (testSuite?.testcases || []).map((testcase: any) => testcase.testcaseId),
    );

    const duplicateTestcaseIds = selectedUniqueIds.filter((testcaseId) =>
      existingTestcaseIds.has(testcaseId),
    );
    const testcaseIdsToAdd = selectedUniqueIds.filter(
      (testcaseId) => !existingTestcaseIds.has(testcaseId),
    );

    if (!testcaseIdsToAdd.length) {
      notifyWarning("Selected scripts already exist in this suite");
      return;
    }

    if (duplicateTestcaseIds.length) {
      notifyWarning(
        `${duplicateTestcaseIds.length} script(s) already exist and will be skipped`,
      );
    }

    notifyInProgress("Adding scripts to test suite");
    const success = await addTestCaseToTestSuite(
      accountId,
      [testSuiteId],
      testcaseIdsToAdd,
    );
    notification.destroy();

    if (!success) {
      notifyError("Failed to add scripts to test suite");
      return;
    }

    closeModal();
    await refresh();
    notifySuccess("Scripts added to test suite successfully");
  };

  return (
    <TestSuiteScriptSelectorModal
      open={open}
      close={close}
      handleRefresh={handleRefresh}
      title="Add script to execution suite"
      submitButtonText="Add script"
      onSubmit={handleAddScript}
    />
  );
};

export default AddScriptToSuiteModal;
